<template>
  <Modal
    inner-class="rounded flex flex-col h-full pb-8"
    custom-class="w-full max-w-[768px]"
    size="none"
    is-add-to-basket-popover
    @close="closeAddToBasketFlyout"
  >
    <div class="flex h-full flex-col pt-8">
      <div class="flex shrink-0 flex-row items-center px-4 py-6">
        <IconCheckmark class="size-6 pr-2" />
        <Headline tag="p" class="text-base sm:text-xl" :is-uppercase="false">
          {{ $t('basket.add_to_basket_info') }}
        </Headline>
      </div>
      <div class="px-4 pb-4">
        <hr class="w-full border-t border-black-10" />
      </div>
      <div class="flex-1 overflow-y-auto">
        <ClientOnly>
          <div class="relative">
            <article
              v-if="lastItemAddedToBasket"
              :key="lastItemAddedToBasket.key"
            >
              <BasketCardPopover
                is-light-variant
                :product="lastItemAddedToBasket.product"
                :variant="lastItemAddedToBasket.variant"
                :title="
                  getBasketCardPopoverTitle(
                    lastItemAddedToBasket.product.attributes,
                  )
                "
                :size="getBasketCardPopoverSize(lastItemAddedToBasket.variant)"
                :color="
                  getProductColors(lastItemAddedToBasket.product, 'color').join(
                    '/',
                  )
                "
                :article-no="lastItemAddedToBasket.variant.referenceKey || ''"
                :image-hash="
                  getFrontImage(lastItemAddedToBasket.product.images).hash
                "
                :reduced-price="getDiscountPrice(lastItemAddedToBasket.price)"
                :price="lastItemAddedToBasket.price.total.withTax"
                :quantity="lastItemAddedToBasket.quantity"
                :available-quantity="lastItemAddedToBasket.availableQuantity"
                :show-price-reduction="isActiveSalePeriod"
                :is-sold-out="lastItemAddedToBasket.product.isSoldOut"
                :has-basket-action="false"
                :has-quantity-action="false"
                :has-wishlist-action="false"
                :is-add-to-basket-popover="isAddToBasketFlyoutOpen"
                :order-number="
                  (lastItemAddedToBasket?.customData as BasketItemCustomData)
                    ?.ordernumber
                "
                @close="closeAddToBasketFlyout"
              >
                <template v-if="isAddToBasketFlyoutOpen">
                  <BasketCTAs
                    is-add-to-basket-popover
                    class="hidden sm:flex"
                    @close="closeAddToBasketFlyout"
                  />
                </template>
              </BasketCardPopover>
            </article>

            <BasketCTAs
              class="sm:hidden"
              is-add-to-basket-popover
              @close="closeAddToBasketFlyout"
            />
          </div>
        </ClientOnly>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import type { Variant, Attributes } from '@scayle/storefront-nuxt'
import {
  getAppliedReductionsByCategory,
  getFirstAttributeValue,
  getSizeFromVariant,
  getProductColors,
} from '@scayle/storefront-nuxt'
import { fetchOnNavigation } from '~/utils/loading'
import useSmartShopConfiguration from '~/composables/useSmartShopConfiguration'
import useUiState from '~/composables/ui/useUiState'
import useAgsBasket from '~/composables/useAgsBasket'
import { getFrontImage } from '~/utils/image'
import type { BasketItemCustomData } from '~/types/basket'

const basket = useAgsBasket()
const {
  closeAddToBasketFlyout,
  isAddToBasketFlyoutOpen,
  lastVariantIdAddedToBasket,
} = useUiState()

const { isActiveSalePeriod } = useSmartShopConfiguration()
const lastItemAddedToBasket = basket.items.value.length
  ? (basket.items.value.find(
      (p) => p.variant.id === lastVariantIdAddedToBasket.value,
    ) ?? basket.items.value[0])
  : null

// Fetch data
await fetchOnNavigation(basket.fetch)

const getDiscountPrice = (price: any) =>
  getAppliedReductionsByCategory(price.total, 'sale')[0]?.amount.absoluteWithTax

const getBasketCardPopoverTitle = (attrs?: Attributes) =>
  getFirstAttributeValue(attrs, 'name')?.label || ''

const getBasketCardPopoverSize = (variant: Variant) =>
  getSizeFromVariant(variant, 'size')?.label || ''
</script>
